import React, { useMemo } from 'react';
import { Download, XCircle } from 'lucide-react';
import SelectionActionBar, {
  type SelectionAction,
} from '../../SelectionActionBar';
import { useStore } from '../../../store';
import { useTakeOverAvailable } from '../../../hooks/useTakeOverAvailable';
import type { Patient } from '../../../store/slices/patientsSlice';
import {
  cancelPatientExternalEmrImportAndPublish,
  startPatientExternalEmrImports,
} from '../../../hooks/usePatientExternalEmrImport';
import { getBusinessErrorMessage } from '../../../utils/apiErrorDetail';
import {
  CANCEL_IMPORTS_ERROR,
  CARD_STATUS_LABEL,
  NONE_TO_CANCEL_REASON,
  NONE_TO_START_REASON,
  START_BLOCKED_NOTICE,
  START_IMPORT_ERROR,
  START_PENDING_REASON,
  bulkCancelResultLine,
  bulkStartResultLine,
  cancelImportsLabel,
  startImportsLabel,
} from './externalEmrPatientCopy';
import {
  bulkStartConfirm,
  cancellable,
  liveCardStatus,
  refusalReasons,
  startable,
  type ExternalPatient,
} from './externalEmrSelection';

interface ExternalEmrSelectionBarProps {
  items: ExternalPatient[];
  onClear: () => void;
  onRemoveItem: (patient: Patient) => void;
  /** Keep only these ids selected (refused starts stay for a retry). */
  onKeepOnly: (ids: string[]) => void;
  onChanged: () => void;
  /** Why the agency cannot import now: Start is disabled with it; Cancel works. */
  startBlockedReason?: string | null;
  /** The readiness read has not answered yet: Start waits, like the cards. */
  startPending?: boolean;
}

/**
 * Multi-select start and cancel for External EMR patients. A selection always
 * confirms first: it shows the count and says the charts run at bulk
 * priority, so a single urgent import still goes ahead of them.
 */
export const ExternalEmrSelectionBar: React.FC<
  ExternalEmrSelectionBarProps
> = ({
  items,
  onClear,
  onRemoveItem,
  onKeepOnly,
  onChanged,
  startBlockedReason = null,
  startPending = false,
}) => {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const takeOverV2 = useTakeOverAvailable();

  const actions = useMemo<SelectionAction<ExternalPatient>[]>(
    () => [
      {
        key: 'start',
        icon: Download,
        label: selected => startImportsLabel(startable(selected).length),
        disabledReason: selected =>
          startBlockedReason ??
          (startPending ? START_PENDING_REASON : undefined) ??
          (startable(selected).length === 0 ? NONE_TO_START_REASON : undefined),
        confirm: selected => bulkStartConfirm(selected, takeOverV2),
        run: async selected => {
          const ids = startable(selected).map(p => p.id);
          try {
            const rows = await startPatientExternalEmrImports(ids);
            const refused = rows.filter(row => row.refused_reason);
            if (refused.length > 0) {
              console.error('External EMR imports refused:', refused);
            }
            const message = bulkStartResultLine(
              rows.length - refused.length,
              refused.length
            );
            if (refused.length > 0) {
              showError(message, refusalReasons(refused));
            } else {
              showSuccess(message);
            }
            onKeepOnly(refused.map(row => row.patient_id));
            onChanged();
          } catch (error) {
            console.error('Failed to start External EMR imports:', error);
            showError(getBusinessErrorMessage(error) ?? START_IMPORT_ERROR);
          }
        },
      },
      {
        key: 'cancel',
        icon: XCircle,
        label: selected => cancelImportsLabel(cancellable(selected).length),
        isVisible: selected => cancellable(selected).length > 0,
        disabledReason: selected =>
          cancellable(selected).length === 0
            ? NONE_TO_CANCEL_REASON
            : undefined,
        run: async selected => {
          const targets = cancellable(selected);
          const results = await Promise.allSettled(
            targets.map(p => cancelPatientExternalEmrImportAndPublish(p.id))
          );
          const failedIds = targets
            .filter((_, i) => results[i].status === 'rejected')
            .map(p => p.id);
          if (failedIds.length > 0) {
            console.error(
              'Failed to cancel External EMR imports:',
              results.filter(r => r.status === 'rejected')
            );
            showError(CANCEL_IMPORTS_ERROR);
          } else {
            showSuccess(bulkCancelResultLine(targets.length));
          }
          onKeepOnly(failedIds);
          onChanged();
        },
      },
    ],
    [
      onChanged,
      onKeepOnly,
      showError,
      showSuccess,
      startBlockedReason,
      startPending,
      takeOverV2,
    ]
  );

  return (
    <SelectionActionBar<ExternalPatient>
      items={items}
      onClear={onClear}
      onRemoveItem={onRemoveItem}
      actions={actions}
      getItemKey={p => p.id}
      getItemLabel={p => p.name || p.mrn}
      notice={startBlockedReason ? START_BLOCKED_NOTICE : null}
      getItemGroup={p => {
        const status = liveCardStatus(p);
        return { key: status, label: CARD_STATUS_LABEL[status] };
      }}
    />
  );
};
